import { useMemo, useRef } from 'react';

import type { ScrollAreaHandle } from 'components/ScrollArea/ScrollArea';
import { ScrollArea } from 'components/ScrollArea/ScrollArea';
import { useI18n } from 'i18n/hooks/useI18n';
import type { ApiJob } from 'modules/listing/jobs/api/types/ApiJob';
import type { SearchContextHeaderData } from 'modules/search/containers/SearchContextHeader/SearchContextHeaderData';
import {
  useIsSearching,
  useSearchCurrentPageResults,
  useSearchFilterType,
} from 'modules/search/contexts/SearchContext';
import { useSaveSearch } from 'modules/search/hooks/useSaveSearch';
import type { SearchSavedListingsStore } from 'modules/search/zustand-stores/searchSavedListingsStore';
import { spacing } from 'theme/theme';
import { AdSection } from 'types/Ads/AdSection';
import { useAdType } from 'zustand-stores/adStore';

import { ViewJobPageSidebarPagination } from './Pagination/ViewJobPageSidebarPagination';
import { ViewJobPageSidebarSuggestions } from './Suggestions/ViewJobPageSidebarSuggestions';
import {
  ViewJobPageSidebarResultsHeaderTitle,
  ViewJobPageSidebarResultsHolder,
  ViewJobPageSidebarSuggestionsHeader,
} from './ViewJobPageSidebarResults.styled';
import { ViewJobPageSidebarResultsHeader } from './ViewJobPageSidebarResultsHeader';

type Props = {
  job: ApiJob;
  searchContext: SearchContextHeaderData | null | undefined;
  searchSavedListingsStore: SearchSavedListingsStore;
};

export function ViewJobPageSidebarResults({
  job,
  searchContext: unstableSearchContext,
  searchSavedListingsStore,
}: Props) {
  const { i18n } = useI18n();

  /**
   * We only show the job search context on the sidebar if the user is coming
   * from a "job" search. If it's an "all" search, we don't show it because there
   * might be other types of results that aren't jobs.
   */
  const isJobSearch = useSearchFilterType() === 'JOB';

  const results = useSearchCurrentPageResults();
  const isSearching = useIsSearching();
  // The search context gets unset briefly when selecting a listing from a different page
  const searchContext = useRef(unstableSearchContext).current;
  const scrollAreaRef = useRef<ScrollAreaHandle>(null);
  const adType = useAdType(AdSection.BANNER);

  const { saveSearch } = useSaveSearch();

  // Height that the sidebar can't use, will be simpler post AB test
  const reservedHeight = useMemo(() => {
    const navHeight = 139;
    const adHeight = 116;
    const filterHeight = 70;
    const sidebarHeaderHeight = 72;

    let height = navHeight + sidebarHeaderHeight;

    height += filterHeight;

    if (adType !== null) {
      height += adHeight;
    }

    return height;
  }, [adType]);

  return (
    <ViewJobPageSidebarResultsHolder>
      {searchContext && isJobSearch ? (
        <ViewJobPageSidebarResultsHeader
          results={results}
          isSearching={isSearching}
          saveSearch={saveSearch}
        />
      ) : (
        <ViewJobPageSidebarSuggestionsHeader>
          <ViewJobPageSidebarResultsHeaderTitle data-qa-id="related-jobs-title">
            {i18n('Related Jobs')}
          </ViewJobPageSidebarResultsHeaderTitle>
        </ViewJobPageSidebarSuggestionsHeader>
      )}
      <ScrollArea
        qaId="sidebar-search-results"
        ref={scrollAreaRef}
        direction="vertical"
        height={`calc(100vh - ${reservedHeight}px)`}
        scrollShadowSize={spacing[24]}
        showScrollShadowEnd
        showScrollShadowStart
      >
        {searchContext && isJobSearch ? (
          <ViewJobPageSidebarPagination
            jobId={job.id}
            results={results}
            searchContext={searchContext}
            scrollingParentRef={scrollAreaRef}
            searchSavedListingsStore={searchSavedListingsStore}
          />
        ) : (
          <ViewJobPageSidebarSuggestions
            job={job}
            searchSavedListingsStore={searchSavedListingsStore}
          />
        )}
      </ScrollArea>
    </ViewJobPageSidebarResultsHolder>
  );
}
